import * as React from 'react';
import { Box } from '@mui/material'
import { type DragHandlers, type MotionValue, motion, useMotionValue } from 'framer-motion'
import { type ReactNode, useCallback, useEffect } from 'react'
import type { ControlsPlacement } from '../GlobalControlsProvider.js'

interface Props {
  initialSize?: number
  minSize?: number
  children?: ReactNode
  placement: ControlsPlacement
  disabled?: boolean
}

export function ResizablePanel(props: Props) {
  const { children, initialSize, minSize, placement, disabled } = props

  let defaultMinSize = 50
  let defaultSize = 50

  switch (placement) {
    case 'bottom':
      defaultSize = initialSize ?? 300
      defaultMinSize = minSize ?? 50
      break

    case 'top':
      defaultSize = initialSize ?? 100
      defaultMinSize = minSize ?? 50
      break

    case 'right':
      defaultSize = initialSize ?? 300
      defaultMinSize = minSize ?? 150
      break

    case 'left':
      defaultSize = initialSize ?? 50
      defaultMinSize = minSize ?? 50
      break
  }

  const mSize = useMotionValue(defaultSize)
  useEffect(() => {
    if (initialSize !== undefined) {
      mSize.set(initialSize)
    }
  }, [mSize, initialSize])
  const direction = placement === 'top' || placement === 'bottom' ? 'vertical' : 'horizontal'

  if (disabled) {
    return <>{children}</>
  }

  return (
    <motion.div
      data-id="resizable-panel"
      style={{
        flex: '0 0 auto',
        width: direction === 'horizontal' ? mSize : '100%',
        height: direction === 'horizontal' ? '100%' : mSize,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {children}
      <ResizeHandle mSize={mSize} minSize={defaultMinSize} resetSize={defaultSize} placement={placement} />
    </motion.div>
  )
}

type ResizeHandleProps = {
  resetSize: number
  mSize: MotionValue<number>
  minSize: number
  placement: ControlsPlacement
}

function ResizeHandle(props: ResizeHandleProps) {
  const { mSize, minSize, resetSize, placement } = props

  const direction = placement === 'top' || placement === 'bottom' ? 'vertical' : 'horizontal'

  const handleOnResize = useCallback<Required<DragHandlers>['onDrag']>(
    (_event, info) => {
      const directionModifier =
        direction === 'horizontal' ? (placement === 'right' ? -1 : 1) : placement === 'bottom' ? -1 : 1
      const delta = direction === 'horizontal' ? info.delta.x : info.delta.y
      const newSize = mSize.get() + delta * directionModifier

      if (newSize >= minSize) mSize.set(newSize)
    },
    [direction, mSize, minSize, placement],
  )

  const handleReset = useCallback(() => {
    mSize.set(resetSize)
  }, [mSize, resetSize])

  return (
    <motion.div
      drag={direction === 'horizontal' ? 'x' : 'y'}
      onDrag={handleOnResize}
      dragElastic={0}
      dragMomentum={false}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      style={{
        [direction === 'horizontal' ? 'top' : 'left']: 0,

        [direction === 'horizontal'
          ? placement === 'left'
            ? 'right'
            : 'left'
          : placement === 'top'
            ? 'bottom'
            : 'top']: 0,

        width: direction === 'horizontal' ? 2 : '100%',
        height: direction === 'horizontal' ? '100%' : 2,
        cursor: 'row-resize',
        zIndex: 90,
        position: 'absolute',
        backgroundColor: 'rgb(230, 230, 230, 0)',
      }}
      whileHover={{
        transition: { duration: 0.1 },
        backgroundColor: 'rgb(230, 230, 230, 1)',
      }}
      whileDrag={{
        transition: { duration: 0.1 },
        backgroundColor: 'rgb(230, 230, 230, 1)',
      }}
    >
      <Box
        sx={{
          display: 'block',
          position: 'absolute',
          top: 0,
          left: 0,
          width: direction === 'horizontal' ? 2 : '100%',
          height: direction === 'horizontal' ? '100%' : 2,
          backgroundColor: 'rgb(230, 230, 230, 1)',
          transition: 'background 0.3s ease, height 0.3s ease',

          '&:hover': {
            backgroundColor: 'rgb(170, 170, 170, 1)',
            [direction === 'horizontal' ? 'width' : 'height']: 4,
          },
        }}
        onDoubleClick={handleReset}
      />
    </motion.div>
  )
}
